'use client';

import { c as _c } from "react/compiler-runtime";
import type { Time } from '@morpho-org/morpho-ts';
import type { Hex } from 'viem';
import { parseApiAsset } from '@morpho-org/dapp-core';
import { InfoTooltip, Select } from '@morpho-org/ui-v6/client';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { cn, formatters } from '@morpho-org/ui-v6/utils';
import { SegmentedControlItem, SegmentedControlList, SuspenseErrorBoundary, Tabs } from '@repo/ui';
import { useQueryErrorResetBoundary, useSuspenseQuery } from '@tanstack/react-query';
import { useState, useTransition } from 'react';
import type { HistoryRange } from '@/lib/transforms/history/window';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { NO_LEND_POINTS } from '@/components/charts/helpers/getPoints';
import { createPlotlineLabelFormatter, formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { useMarketPoints } from '@/components/charts/hooks/useMarketPoints';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { IconRewardHover } from '@/components/common/IconRewardHover';
import { timeFrameOptions } from '@/config/table';
import { toHistoryRange } from '@/lib/transforms/history/toHistoryRange';
import { type MarketApyRateMode, useApyCardMarketRateMode } from '../ApyCardMarketRateMode.context';
import { type MarketApyCardData, selectMarketApyRates } from '../MarketApyCard.mapper';
import { DEFAULT_MARKET_APY_HISTORY_TIMEFRAME, marketApyHistoryQueryOptions } from './marketApyHistoryQuery';
const CHART_HEIGHT = '225px';
const RATE_MODE_OPTIONS: {
  value: MarketApyRateMode;
  label: string;
}[] = [{
  value: 'borrow',
  label: 'Borrow'
}, {
  value: 'supply',
  label: 'Lend'
}];
export interface ChartApyCardMarketProps {
  market: MarketApyCardData;
}

/**
 * Chart component to display the market apy during the time
 */
const ChartApyCardMarketClient = t0 => {
  const $ = _c(85);
  const {
    market
  } = t0;
  const {
    rateMode,
    setRateMode
  } = useApyCardMarketRateMode();
  const isBorrowMode = rateMode === "borrow";
  const [timeFrame, setTimeFrame] = useState(DEFAULT_MARKET_APY_HISTORY_TIMEFRAME);
  const [isRangePending, startTransition] = useTransition();
  let t1;
  if ($[0] !== timeFrame) {
    t1 = toHistoryRange(timeFrame);
    $[0] = timeFrame;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const range = t1;
  const {
    reset: resetQueryErrors
  } = useQueryErrorResetBoundary();
  let apy;
  let collateralRewards;
  let dailyApy;
  let instantApy;
  let netApy;
  let rewards;
  let t2;
  let weeklyApy;
  if ($[2] !== market || $[3] !== rateMode) {
    ({
      apy,
      instantApy,
      netApy,
      dailyApy,
      weeklyApy,
      rewards,
      collateralRewards
    } = selectMarketApyRates(market, rateMode));
    t2 = formatters.percent.default("-").digits(2).max(10).of(netApy, 18);
    $[2] = market;
    $[3] = rateMode;
    $[4] = apy;
    $[5] = collateralRewards;
    $[6] = dailyApy;
    $[7] = instantApy;
    $[8] = netApy;
    $[9] = rewards;
    $[10] = t2;
    $[11] = weeklyApy;
  } else {
    apy = $[4];
    collateralRewards = $[5];
    dailyApy = $[6];
    instantApy = $[7];
    netApy = $[8];
    rewards = $[9];
    t2 = $[10];
    weeklyApy = $[11];
  }
  const formattedNetApy = t2;
  const t3 = market.collateralAsset?.address;
  let t4;
  if ($[12] !== market.chainId || $[13] !== market.marketId || $[14] !== t3) {
    t4 = {
      chainId: market.chainId,
      marketId: market.marketId,
      collateralAddress: t3
    };
    $[12] = market.chainId;
    $[13] = market.marketId;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const borrowPoints = useMarketPoints(t4);
  const points = isBorrowMode ? borrowPoints : NO_LEND_POINTS;
  const t5 = isBorrowMode ? "market" as const : "supply" as const;
  const t6 = isBorrowMode ? market.collateralAsset : market.loanAsset;
  let t7;
  if ($[16] !== t6) {
    t7 = parseApiAsset(t6);
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== apy || $[19] !== collateralRewards || $[20] !== dailyApy || $[21] !== instantApy || $[22] !== netApy || $[23] !== points || $[24] !== rewards || $[25] !== t5 || $[26] !== t7 || $[27] !== weeklyApy) {
    t8 = {
      apy,
      rewards,
      points,
      variant: t5,
      netApy,
      asset: t7,
      collateralRewards,
      instantApy,
      dailyApy,
      weeklyApy
    };
    $[18] = apy;
    $[19] = collateralRewards;
    $[20] = dailyApy;
    $[21] = instantApy;
    $[22] = netApy;
    $[23] = points;
    $[24] = rewards;
    $[25] = t5;
    $[26] = t7;
    $[27] = weeklyApy;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  const rewardsHoverCardProps = t8;
  const t9 = `${market.chainId}:${market.marketId}:${rateMode}`;
  const t10 = isBorrowMode ? "Borrow Rate (6h)" : "Lend Rate (6h)";
  let t11;
  if ($[29] !== t10) {
    t11 = <span className="text-secondary-foreground text-xs leading-normal">{t10}</span>;
    $[29] = t10;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== rewardsHoverCardProps || $[32] !== t11 || $[33] !== t9) {
    t12 = <IconRewardHover key={t9} RewardsHoverCardProps={rewardsHoverCardProps} noHover={true}>{t11}</IconRewardHover>;
    $[31] = rewardsHoverCardProps;
    $[32] = t11;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const t13 = isBorrowMode ? "The total rate paid by borrowers, including token rewards." : "The total rate earned by lenders, including token rewards.";
  let t14;
  if ($[35] !== t13) {
    t14 = <InfoTooltip tooltipContent={t13} />;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t12 || $[38] !== t14) {
    t15 = <div className="flex items-center gap-[2px]">{t12}{t14}</div>;
    $[37] = t12;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== formattedNetApy) {
    t16 = <span data-testid="market-apy-card-net-rate" className="text-title-sm text-foreground">{formattedNetApy}</span>;
    $[40] = formattedNetApy;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="text-title-sm text-muted-foreground">%</span>;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t16) {
    t18 = <div className="flex items-center gap-[2px]">{t16}{t17}</div>;
    $[43] = t16;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t15 || $[46] !== t18) {
    t19 = <div className="gap-xs flex flex-col">{t15}{t18}</div>;
    $[45] = t15;
    $[46] = t18;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== setRateMode) {
    t20 = value => setRateMode(value as MarketApyRateMode);
    $[48] = setRateMode;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SegmentedControlList size="default" aria-label="Rate">{RATE_MODE_OPTIONS.map(_temp)}</SegmentedControlList>;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== rateMode || $[52] !== t20) {
    t22 = <Tabs value={rateMode} onValueChange={t20}>{t21}</Tabs>;
    $[51] = rateMode;
    $[52] = t20;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== resetQueryErrors) {
    t23 = value_0 => {
      resetQueryErrors();
      startTransition(() => setTimeFrame(value_0));
    };
    $[54] = resetQueryErrors;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t23 || $[57] !== timeFrame) {
    t24 = <Select options={timeFrameOptions} value={timeFrame} onChange={t23} />;
    $[56] = t23;
    $[57] = timeFrame;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t22 || $[60] !== t24) {
    t25 = <div className="gap-sm flex flex-wrap items-center">{t22}{t24}</div>;
    $[59] = t22;
    $[60] = t24;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t19 || $[63] !== t25) {
    t26 = <div className="gap-sm flex flex-wrap items-center justify-between">{t19}{t25}</div>;
    $[62] = t19;
    $[63] = t25;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  const t27 = isRangePending && "opacity-50";
  let t28;
  if ($[65] !== t27) {
    t28 = cn("transition-opacity", t27);
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== range) {
    t29 = [range];
    $[67] = range;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Skeleton className="w-full rounded-[20px]" style={{
      height: CHART_HEIGHT
    }} />;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div data-testid="market-apy-chart-error"><ErrorDataUnavailable minHeight={CHART_HEIGHT} showActions={false} /></div>;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== market.chainId || $[72] !== market.marketId || $[73] !== range || $[74] !== rateMode) {
    t32 = <MarketApyHistoryChart key={range} chainId={market.chainId} marketId={market.marketId} range={range} rateMode={rateMode} />;
    $[71] = market.chainId;
    $[72] = market.marketId;
    $[73] = range;
    $[74] = rateMode;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== t29 || $[77] !== t32) {
    t33 = <SuspenseErrorBoundary resetKeys={t29} fallback={t30} errorFallback={t31}>{t32}</SuspenseErrorBoundary>;
    $[76] = t29;
    $[77] = t32;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== t28 || $[80] !== t33) {
    t34 = <div className={t28}>{t33}</div>;
    $[79] = t28;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t26 || $[83] !== t34) {
    t35 = <div data-testid="market-apy-card" className="gap-lg flex min-w-[220px] grow flex-col">{t26}{t34}</div>;
    $[82] = t26;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  return t35;
};
interface MarketApyHistoryChartProps {
  chainId: number;
  marketId: Hex;
  range: HistoryRange;
  rateMode: MarketApyRateMode;
}
const MarketApyHistoryChart = t0 => {
  const $ = _c(26);
  const {
    chainId,
    marketId,
    range,
    rateMode
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  let t1;
  if ($[0] !== chainId || $[1] !== marketId || $[2] !== range) {
    t1 = marketApyHistoryQueryOptions({
      chainId,
      marketId,
      range
    });
    $[0] = chainId;
    $[1] = marketId;
    $[2] = range;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  const isBorrowMode = rateMode === "borrow";
  let t2;
  if ($[4] !== data.points || $[5] !== isBorrowMode) {
    let t3;
    if ($[7] !== isBorrowMode) {
      t3 = point => ({
        x: point.timestamp,
        y: isBorrowMode ? point.netBorrowApy : point.netSupplyApy
      });
      $[7] = isBorrowMode;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t2 = formatTimeseriesData(data.points.map(t3));
    $[4] = data.points;
    $[5] = isBorrowMode;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const formattedData = t2;
  const averageApy = isBorrowMode ? data.averageNetBorrowApy : data.averageNetSupplyApy;
  let t3;
  if ($[9] !== averageApy) {
    t3 = formatters.percent.default("-").digits(2).max(10).of(averageApy);
    $[9] = averageApy;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const formattedAvgApy = t3;
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      loading: false
    };
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== formattedData || $[13] !== palette.areaChartColors[0] || $[14] !== palette.colors[0]) {
    t5 = [{
      type: "areaspline",
      name: "Apy",
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[12] = formattedData;
    $[13] = palette.areaChartColors[0];
    $[14] = palette.colors[0];
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = averageApy ?? undefined;
  const t7 = `Avg ${formattedAvgApy}%`;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = createPlotlineLabelFormatter("var(--foreground)");
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7) {
    t9 = {
      x: 6,
      y: -15,
      text: t7,
      align: "left",
      useHTML: true,
      formatter: t8
    };
    $[17] = t7;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t9) {
    t10 = [{
      value: t6,
      className: "hc-plotline-brand",
      color: "var(--primary)",
      dashStyle: "Dot",
      width: 1,
      label: t9
    }];
    $[19] = t6;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      yAxis: {
        softMin: 0,
        maxPadding: 0.4
      },
      chart: {
        plotBackgroundColor: "var(--muted)"
      }
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t5) {
    t12 = <div data-testid="market-apy-chart"><SimpleChart statuses={t4} unit="%" height={CHART_HEIGHT} series={t5} plotlinesY={t10} additionalOptions={t11} /></div>;
    $[23] = t10;
    $[24] = t5;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
export default ChartApyCardMarketClient;
function _temp(option) {
  return <SegmentedControlItem key={option.value} value={option.value}>{option.label}</SegmentedControlItem>;
}